import {Platforms} from '../models/interfaces';
import {DeepEnum} from '../elf-store/user-permissions/user-permissions.type';

export function getFirstLetters(str: string): string {
  try {
    return str
      .split(' ')
      .map(word => word[0])
      .join('');
  } catch (e) {
    throw new Error(`helpers - getFirstLetters ${e.message}`);
  }
}

export function getOS(): string {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator['userAgentData']?.platform || window.navigator.platform;
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K', 'macOS'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

  let os = '';

  if (macosPlatforms.includes(platform)) {
    os = Platforms.MacOS;
  } else if (iosPlatforms.includes(platform)) {
    os = Platforms.iOS;
  } else if (windowsPlatforms.includes(platform)) {
    os = Platforms.Windows;
  } else if (/Android/.test(userAgent)) {
    os = Platforms.Android;
  } else if (/Linux/.test(platform)) {
    os = Platforms.Linux;
  }

  return os;
}

export function getHostFromUrl(url: string) {
  if (!url) {
    return;
  }

  return url
    .replace(/(^\w+:|^)\/\//, '')
    .replace(/^(www\.)/, '')
    .split('/')[0];
}

export function getYoutubeVideoLink(id: string) {
  return `youtu.be/${id}`;
}

export const convertToSlug = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, '_');
};

export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function findValueByKey(obj: Record<string, any>, key: string) {
  const isObj = val => typeof val === 'object';
  if (!obj) return;

  if (obj.hasOwnProperty(key)) {
    return obj[key];
  }

  for (let prop in obj) {
    const curr = obj[prop];
    if (isObj(curr)) {
      const result = findValueByKey(curr, key);
      if (result !== null) {
        return result;
      }
    }
  }

  return null;
}

export function constructQueryString(queryParams: Record<string, any>): string {
  const params = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (!value) return;
    params.append(key, value.toString());
  });
  return params.toString();
}

export function areObjectsEqual(obj1: Record<string, any>, obj2: Record<string, any>): boolean {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

export function generatePathToPermissions<T>(obj: T, path: string = ''): DeepEnum<T> {
  const result: any = {};
  for (const key in obj) {
    const fullPath = path ? `${path}.${key}` : key;
    const value = obj[key];
    if (typeof value === 'object' && value !== null) {
      result[key] = generatePathToPermissions(value, fullPath);
    } else {
      result[key] = fullPath;
    }
  }
  return result;
}

export function scrollToTop(mainContainer: HTMLElement = document.querySelector('.main-drawer-content')) {
  mainContainer.scrollTo({top: 0});
}

export function areObjectsDeeplyEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;

  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false;
  }

  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (let key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!areObjectsDeeplyEqual(a[key], b[key])) return false;
  }

  return true;
}
